import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * 合并 Tailwind CSS 类名
 * 使用 clsx 进行条件合并，使用 tailwind-merge 解决类名冲突
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** 汇总时长（秒 -> mm:ss）：先四舍五入总秒数，再计算分秒以保留进位。 */
export function formatDuration(seconds: number): string {
  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainder = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

/**
 * 格式化剩余时间（秒）为可读文案
 * >= 60 显示「X 分 Y 秒」，< 60 显示「X 秒」
 */
export function formatRemainingTime(seconds: number): string {
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return s > 0 ? `${m} 分 ${s} 秒` : `${m} 分钟`;
  }
  return `${Math.floor(seconds)} 秒`;
}

/**
 * 生成 UUID v4
 * 优先使用 crypto.randomUUID，其次降级到 crypto.getRandomValues
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (e) {
      // 某些环境下可能报错，降级处理
      console.warn('crypto.randomUUID failed, falling back to Math.random', e);
    }
  }

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    return Array.from(bytes, (byte, index) => {
      const prefix = [4, 6, 8, 10].includes(index) ? '-' : '';
      return `${prefix}${byte.toString(16).padStart(2, '0')}`;
    }).join('');
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
